
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { WalletType } from '../../types';
import { Shield } from 'lucide-react';
import { 
  WalletPhantom, 
  WalletSolflare, 
  WalletWalletConnect, 
  WalletCoinbase
} from '@web3icons/react';

// High-fidelity SVG implementations for a "real" financial look
const SolanaIconLocal = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className={className}>
        <path fill="url(#SOL__a)" d="M18.413 7.903a.62.62 0 0 1-.411.164H3.58c-.512 0-.77-.585-.416-.929l2.368-2.283a.6.6 0 0 1 .41-.169h14.479c.517 0 .77.59.41.934z"/>
        <path fill="url(#SOL__b)" d="M18.413 19.157a.6.6 0 0 1-.411.157H3.58c-.512 0-.77-.58-.416-.923l2.368-2.289a.6.6 0 0 1 .41-.163h14.479c.517 0 .77.585.41.928z"/>
        <path fill="url(#SOL__c)" d="M18.413 10.472a.6.6 0 0 0-.411-.158H3.58c-.512 0-.77.58-.416-.922l2.368 2.29a.62.62 0 0 0 .41.163h14.479c.517 0 .77-.585.41-.928z"/>
        <defs>
            <linearGradient id="SOL__a" x1="3.001" x2="21.431" y1="16.322" y2="15.591" gradientUnits="userSpaceOnUse">
                <stop stopColor="#599DB0"/>
                <stop offset="1" stopColor="#47F8C3"/>
            </linearGradient>
            <linearGradient id="SOL__b" x1="3.001" x2="21.323" y1="16.973" y2="16.366" gradientUnits="userSpaceOnUse">
                <stop stopColor="#C44FE2"/>
                <stop offset="1" stopColor="#73B0D0"/>
            </linearGradient>
            <linearGradient id="SOL__c" x1="4.035" x2="20.302" y1="12.002" y2="12.002" gradientUnits="userSpaceOnUse">
                <stop stopColor="#778CBF"/>
                <stop offset="1" stopColor="#5DCDC9"/>
            </linearGradient>
        </defs>
    </svg>
);

const SuiIconLocal = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={className}>
        <circle cx="12" cy="12" r="12" fill="#4BA2FF" />
        <path fill="white" d="M16.129 10.508a5.44 5.44 0 0 1 1.148 3.356 5.47 5.47 0 0 1-1.18 3.399l-.064.079-.015-.106a5 5 0 0 0-.053-.26c-.37-1.656-1.567-3.08-3.547-4.234-1.334-.773-2.102-1.705-2.303-2.763a4.1 4.1 0 0 1 .159-1.97c.15-.494.385-.96.694-1.376l.773-.963a.333.333 0 0 1 .518 0zm1.217-.963L12.19 3.092a.243.243 0 0 0-.38 0L6.652 9.55l-.016.016a7.1 7.1 0 0 0-1.519 4.404C5.118 17.85 8.2 21 12 21s6.881-3.15 6.881-7.03a7.1 7.1 0 0 0-1.519-4.404zm-9.46.942.461-.577.016.106.037.254c.302 1.604 1.366 2.938 3.15 3.97 1.55.905 2.45 1.943 2.71 3.081.106.476.127.942.08 1.35v.026l-.022.01c-.72.36-1.513.547-2.318.546-2.912 0-5.278-2.414-5.278-5.389 0-1.275.434-2.45 1.165-3.377"/>
    </svg>
);

const UsdcIconLocal = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className={className}>
        <path fill="#2775CA" d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18"/>
        <path fill="#fff" d="M13.62 5.45v1.159a5.64 5.64 0 0 1 4.005 5.394 5.64 5.64 0 0 1-4.005 5.394v1.16a6.74 6.74 0 0 0 5.13-6.554 6.74 6.74 0 0 0-5.13-6.553m-7.245 6.553a5.64 5.64 0 0 1 4.005-5.394V5.45a6.74 6.74 0 0 0-5.13 6.553 6.74 6.74 0 0 0 5.13 6.553v-1.159a5.63 5.63 0 0 1-4.005-5.394"/>
        <path fill="#fff" d="M14.419 13.258c0-2.301-3.606-1.356-3.606-2.627 0-.456.366-.748 1.063-.748.833 0 1.12.405 1.21.95h1.147c-.102-1.024-.69-1.67-1.67-1.863v-.904h-1.125v.872c-1.075.137-1.75.762-1.75 1.693 0 2.312 3.611 1.445 3.611 2.694 0 .472-.455.787-1.226.787-1.007 0-1.339-.444-1.462-1.057H9.49c.073 1.122.764 1.823 1.947 1.999v.886h1.125v-.875c1.153-.149 1.856-.82 1.856-1.807"/>
    </svg>
);

const CetusIconLocal = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className={className}>
        <path fill="#68FFD8" d="M20.438 11.438c0 4.54-3.78 8.005-8.438 8.005q-.66-.002-1.294-.096c-1.76 1.587-4.736 1.71-6.086 1.637l1.26-4.106a8.08 8.08 0 0 1-2.318-5.648C3.562 6.679 7.342 3 12 3c4.106 0 7.521 2.852 8.28 6.632-.933-.13-2.148-.529-2.475-1.496-.253.562-1.203 1.62-3.077 1.57a3 3 0 0 0 .433-1.57A3.12 3.12 0 0 0 12 5.053a3.12 3.12 0 0 0-3.167 3.083c.006 1.704 1.423 3.302 3.167 3.302z"/>
        <path fill="#00D49E" d="M17.592 11.437c-.98 4.691-5.232 8.004-10.34 8.004q-1.078 0-2.098-.202l-.54 1.744c1.356.073 4.332-.057 6.087-1.637q.64.094 1.3.095c4.657 0 8.437-3.465 8.437-8.004zm.208-3.303c.017-.028-.011.034 0 0 .332.968 1.547 1.367 2.475 1.497a8.23 8.23 0 0 0-3.735-5.344c.663 1.192 1.125 2.435 1.26 3.847"/>
    </svg>
);

const SuietIcon = ({ className }: { className?: string }) => (
    <div className={`rounded-full bg-black flex items-center justify-center text-white ${className}`}>
        <svg width="60%" height="60%" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.0805 5.5484C12.4547 5.5484 10.3708 7.63226 10.3708 10.2581C10.3708 11.6387 11.01 12.8645 12.0487 13.7419L4.3068 16.0645C3.40357 16.3226 3.01653 17.3032 3.2746 18.2065C3.53266 19.1097 4.51329 19.4839 5.41653 19.2258L13.6617 16.9032C13.2747 17.8645 13.4682 18.9935 14.2036 19.8387L9.01653 27.3548C8.33905 28.1806 8.50039 29.4065 9.3262 30.0839C10.152 30.7613 11.3779 30.5935 12.0487 29.7677L17.2294 22.2581C18.1779 22.6323 19.3068 22.4387 20.1456 21.7613C21.7779 20.4194 21.9036 18.0194 20.4359 16.5484C19.5133 15.6258 18.2875 15.2516 17.0617 15.5097L19.384 7.76774C19.642 6.86452 19.2677 5.88387 18.3649 5.62581C17.4617 5.36774 16.481 5.74194 16.223 6.64516L13.9837 14.3226C12.9128 13.4968 12.5649 12.271 12.5649 10.8903C12.5649 8.66452 14.0095 6.83871 16.0165 6.6C17.971 6.3871 19.8095 7.84516 20.0359 9.80645C20.1327 10.5484 19.642 11.2129 18.9004 11.3097C18.1588 11.4065 17.5 10.9161 17.4032 10.1742C17.2581 9.01935 16.223 8.25806 15.2581 8.41935C14.2932 8.57419 13.5327 9.3871 13.6779 10.3548C13.7747 10.8903 13.339 11.4581 12.8036 11.5548C12.5519 11.5806 12.3004 11.5548 12.0487 11.4581L14.2036 19.4194C14.481 20.4019 15.4682 20.9161 16.481 20.6645C17.4939 20.4194 18.0353 19.4387 17.7677 18.4516L15.6133 10.4516C15.9327 10.3871 16.223 10.2419 16.4682 10.0194C17.5068 9.01935 17.3585 7.35484 16.1779 6.29032C16.0165 6.13548 15.842 5.99032 15.6294 5.89677V5.5484Z" fill="currentColor"/>
        </svg>
    </div>
);

export const ChainIcon: React.FC<{ chain: 'SOL' | 'SUI' | string; className?: string }> = ({ chain, className = "w-6 h-6" }) => {
    const renderIcon = () => {
        switch (chain) {
            case 'SOL': return <SolanaIconLocal className="w-full h-full" />;
            case 'SUI': return <SuiIconLocal className="w-full h-full" />;
            default: return <div className="w-full h-full rounded-full bg-gray-700" />;
        }
    };
    return <div className={className}>{renderIcon()}</div>;
};

export const WalletIcon: React.FC<{ wallet: WalletType | string; className?: string }> = ({ wallet, className = "w-10 h-10" }) => {
    const renderIcon = () => {
        switch(wallet) {
            case 'phantom': return <WalletPhantom variant="branded" size="100%" />;
            case 'suiet': return <SuietIcon className="w-full h-full" />;
            case 'solflare': return <WalletSolflare variant="branded" size="100%" />;
            case 'base': return <WalletCoinbase variant="branded" size="100%" />;
            case 'walletconnect': return <WalletWalletConnect variant="branded" size="100%" />;
            default: return <div className="w-full h-full rounded-full bg-gray-300" />;
        }
    };
    return <div className={className}>{renderIcon()}</div>;
};

export const TokenIcon: React.FC<{ symbol: string; fallback?: string; className?: string }> = ({ symbol, fallback, className = "w-6 h-6" }) => {
    const renderIcon = () => {
        const upSymbol = symbol.toUpperCase();
        if (upSymbol === 'VEGHOST') {
            return (
                <div className={`relative ${className}`}>
                    <img src={fallback} alt={symbol} className="w-full h-full object-contain rounded-full bg-white dark:bg-black p-0.5 shadow-sm" />
                    <div className="absolute -bottom-1 -right-1 bg-nobel-gold dark:bg-nobel-gold rounded-full p-0.5 border border-white dark:border-[#0A0B0F] shadow-sm">
                        <Shield size={6} className="text-black" strokeWidth={3} />
                    </div>
                </div>
            );
        }
        
        switch(upSymbol) {
            case 'SOL': return <SolanaIconLocal className={className} />;
            case 'SUI': return <SuiIconLocal className={className} />;
            case 'USDC': return <UsdcIconLocal className={className} />;
            case 'CETUS': return <CetusIconLocal className={className} />;
            case 'GHOST':
            case 'WGHOST':
                return fallback ? <img src={fallback} alt={symbol} className={`${className} object-contain rounded-full shadow-sm bg-white dark:bg-black p-0.5`} /> : <div className={`${className} rounded-full bg-stone-900`} />;
            default: return fallback ? <img src={fallback} alt={symbol} className={`${className} object-contain rounded-full`} /> : <div className={`${className} rounded-full bg-gray-400`} />;
        }
    };
    return renderIcon();
};
